/**
 * Filtro de zona / rango alineado con cocina y reglas de pseudo-zonas Delivery / TakeAway.
 * @param {unknown[]} selectedZoneFilter
 * @param {unknown[]} selectedRangeFilter
 */
export function isKitchenZoneRangeFilterActive(selectedZoneFilter, selectedRangeFilter) {
	const hasZones = Array.isArray(selectedZoneFilter) && selectedZoneFilter.length > 0;
	const hasRanges = Array.isArray(selectedRangeFilter) && selectedRangeFilter.length > 0;
	return hasZones || hasRanges;
}

/**
 * @param {object} reservation
 * @param {{ selectedZoneFilter: string[]; rangeTableNames: string[] }} opts
 */
export function reservationPassesKitchenZoneRangeFilter(reservation, { selectedZoneFilter, rangeTableNames }) {
	const zones = Array.isArray(selectedZoneFilter) ? selectedZoneFilter : [];
	const hasZone = zones.length > 0;
	const rangeSet = new Set(
		(Array.isArray(rangeTableNames) ? rangeTableNames : [])
			.map((n) => String(n).trim())
			.filter(Boolean),
	);
	const hasRange = rangeSet.size > 0;

	if (!hasZone && !hasRange) {
		return true;
	}

	const type = (reservation.type || '').toLowerCase();
	if (type === 'delivery') {
		return zones.includes('Delivery');
	}
	if (type === 'takeaway') {
		return zones.includes('TakeAway');
	}

	let matchZone = false;
	if (hasZone && reservation.roomName) {
		matchZone = zones.includes(reservation.roomName);
	}

	let matchRange = false;
	if (hasRange) {
		const candidates = new Set();
		if (reservation.table != null && reservation.table !== '') {
			candidates.add(String(reservation.table).trim());
		}
		if (Array.isArray(reservation.tables)) {
			reservation.tables.forEach((t) => {
				const n = String(t?.name ?? t).trim();
				if (n) candidates.add(n);
			});
		}
		for (const c of candidates) {
			if (rangeSet.has(c)) {
				matchRange = true;
				break;
			}
		}
	}

	return matchZone || matchRange;
}

function toRangeTableNameSet(rangeTableNames) {
	return new Set(
		(Array.isArray(rangeTableNames) ? rangeTableNames : [])
			.map((n) => String(n).trim())
			.filter(Boolean),
	);
}

function getTableName(table) {
	if (table == null) return '';
	return String(table.name ?? table.table_id ?? table.id ?? table).trim();
}

/**
 * @param {object} table
 * @param {{ selectedZoneFilter: string[]; rangeTableNames: string[] }} opts
 */
export function tablePassesZoneRangeFilter(table, { selectedZoneFilter, rangeTableNames }) {
	const zones = Array.isArray(selectedZoneFilter) ? selectedZoneFilter : [];
	const hasZone = zones.length > 0;
	const rangeSet = toRangeTableNameSet(rangeTableNames);
	const hasRange = rangeSet.size > 0;

	if (!hasZone && !hasRange) {
		return true;
	}

	const matchZone = hasZone && table?.room != null && zones.includes(table.room);
	const tableName = getTableName(table);
	const matchRange = hasRange && tableName !== '' && rangeSet.has(tableName);

	return matchZone || matchRange;
}

/**
 * @param {object[]} tables
 * @param {{ selectedZoneFilter: string[]; selectedRangeFilter: string[]; rangeTableNames: string[] }} opts
 */
export function filterTablesByZoneRange(tables, { selectedZoneFilter, selectedRangeFilter, rangeTableNames }) {
	if (!Array.isArray(tables)) return [];

	if (!isKitchenZoneRangeFilterActive(selectedZoneFilter, selectedRangeFilter)) {
		return tables;
	}

	return tables.filter((table) =>
		tablePassesZoneRangeFilter(table, { selectedZoneFilter, rangeTableNames }),
	);
}

/**
 * @param {string[]} uniqueRooms
 * @param {{ preference?: string; includeBarra?: boolean; barRoomSystemName?: string | null }} opts
 */
export function buildZoneButtonList(uniqueRooms, { preference = 'all', includeBarra = false, barRoomSystemName = null } = {}) {
	const rooms = Array.isArray(uniqueRooms) ? uniqueRooms : [];
	const barRoom =
		barRoomSystemName ??
		(rooms.includes('Barra') ? 'Barra' : rooms.find((zone) => zone.toLowerCase().includes('barra')) ?? null);
	const hasBarra = barRoom != null && rooms.includes(barRoom);
	const filteredOthers = rooms.filter((zone) => zone !== barRoom && zone !== 'Todas');

	let zones = [];

	if (preference === 'all') {
		zones.push('Todas');
		zones = zones.concat(filteredOthers);
		if (includeBarra && hasBarra) zones.push(barRoom);
	} else {
		if (includeBarra && hasBarra) zones.push(barRoom);
		zones = zones.concat(filteredOthers);
		zones.push('Todas');
	}

	return zones;
}
